import { type ChangeEvent, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PartPicker } from '@/components/parts/PartPicker';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useActiveOrgSlug } from '@/lib/activeOrg';
import { useMe } from '@/lib/auth';
import { useBom, useRevisions } from '@/lib/boms';
import {
  type CandidatePart,
  type CommitDecision,
  type CommitResponse,
  type ResolvedRow,
  type ResolveResponse,
  type UploadResponse,
  useCommitBomImport,
  useResolveBom,
  useUploadBom,
} from '@/lib/bomsImport';
import { cn } from '@/lib/cn';
import { type PartListItem } from '@/lib/parts';

type Step = 'upload' | 'map' | 'review' | 'done';

interface RowChoice {
  partId: number | null; // null = skip
  // PartPicker mode: when `pickerActive` is true the row swaps from the
  // candidate dropdown to a free-text part search. `pickerSelected` holds
  // the picker's currently-selected option so the search field can render
  // it without a refetch — only the id flows back to the server.
  pickerActive: boolean;
  pickerSelected: PartListItem | null;
  quantity: string;
  refDes: string; // comma/space-separated for editing
}

export function BomImportPage() {
  const { bomId: bomIdRaw } = useParams<{ bomId: string }>();
  const bomId = bomIdRaw ? Number(bomIdRaw) : null;
  const navigate = useNavigate();

  const me = useMe();
  const orgSlug = useActiveOrgSlug(me.data?.organizations);
  const bom = useBom(orgSlug, bomId);
  const revisions = useRevisions(orgSlug, bomId);

  // Always import into the latest draft revision. If there is no draft
  // (every revision frozen), we tell the user to create a new one first
  // — easier than letting them upload then 409 on commit.
  const latestRev = revisions.data?.[revisions.data.length - 1];
  const draftRevId =
    latestRev && latestRev.frozen_at === null ? latestRev.id : null;

  const upload = useUploadBom(orgSlug, bomId, draftRevId);
  const resolve = useResolveBom(orgSlug, bomId, draftRevId);
  const commit = useCommitBomImport(orgSlug, bomId, draftRevId);

  const [step, setStep] = useState<Step>('upload');
  const [uploaded, setUploaded] = useState<UploadResponse | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [resolved, setResolved] = useState<ResolveResponse | null>(null);
  const [choices, setChoices] = useState<RowChoice[]>([]);
  const [final, setFinal] = useState<CommitResponse | null>(null);

  if (!bomId) return <p className="text-stone-500 dark:text-stone-400">Invalid BOM id.</p>;

  if (revisions.isPending) return <p className="text-stone-500 dark:text-stone-400">Loading…</p>;
  if (!draftRevId) {
    return (
      <NoDraftRevisionMessage bomId={bomId} bomName={bom.data?.name ?? ''} />
    );
  }

  const onFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const result = await upload.mutateAsync(file);
      setUploaded(result);
      setMapping(result.auto_detected_mapping);
      setStep('map');
    } catch {
      /* surfaced below */
    }
  };

  const runResolve = async () => {
    if (!uploaded) return;
    try {
      const result = await resolve.mutateAsync({
        upload_id: uploaded.upload_id,
        column_mapping: mapping,
      });
      setResolved(result);
      setChoices(seedChoices(result.rows));
      setStep('review');
    } catch {
      /* surfaced below */
    }
  };

  const runCommit = async (dryRun: boolean) => {
    if (!uploaded || !resolved) return;
    const decisions = buildDecisions(resolved.rows, choices);
    try {
      const result = await commit.mutateAsync({
        upload_id: uploaded.upload_id,
        decisions,
        dry_run: dryRun,
      });
      if (!dryRun) {
        setFinal(result);
        setStep('done');
      } else {
        setFinal(result);
      }
    } catch {
      /* surfaced below */
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/boms/${bomId}`}
          className="text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-200"
        >
          ← {bom.data?.name ?? 'BOM'}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">
          Import BOM into Rev {latestRev?.revision_label}
        </h1>
        <p className="mt-1 text-stone-600 dark:text-stone-400">
          Upload a CSV/xlsx exported from KiCad (or any tool). The importer
          will look up parts by PartForge part number first, then by
          manufacturer PN, and offer candidates by value/footprint when
          there's no exact match.
        </p>
      </div>

      <Stepper step={step} />

      {step === 'upload' && (
        <UploadStep
          uploading={upload.isPending}
          error={upload.error as Error | null}
          onSelect={onFile}
        />
      )}

      {step === 'map' && uploaded && (
        <MapStep
          upload={uploaded}
          mapping={mapping}
          onChange={setMapping}
          onCancel={() => {
            setUploaded(null);
            setMapping({});
            setStep('upload');
          }}
          onContinue={runResolve}
          resolving={resolve.isPending}
          error={resolve.error as Error | null}
        />
      )}

      {step === 'review' && resolved && (
        <ReviewStep
          orgSlug={orgSlug}
          resolved={resolved}
          choices={choices}
          onChoicesChange={setChoices}
          onBack={() => setStep('map')}
          onCommit={() => runCommit(false)}
          committing={commit.isPending}
          error={commit.error as Error | null}
          preview={final}
        />
      )}

      {step === 'done' && final && (
        <DoneStep result={final} onBack={() => navigate(`/boms/${bomId}`)} />
      )}
    </div>
  );
}

function NoDraftRevisionMessage({
  bomId,
  bomName,
}: {
  bomId: number;
  bomName: string;
}) {
  return (
    <div className="space-y-4">
      <div>
        <Link
          to={`/boms/${bomId}`}
          className="text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-200"
        >
          ← {bomName}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">Import BOM</h1>
      </div>
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        This BOM's latest revision is frozen. Open the BOM and click{' '}
        <span className="font-medium">New revision</span> to start a draft, then
        come back here to import.
      </div>
    </div>
  );
}

function seedChoices(rows: ResolvedRow[]): RowChoice[] {
  return rows.map((row) => ({
    partId: row.matched_part?.part_id ?? null,
    pickerActive: false,
    pickerSelected: null,
    quantity: String(row.quantity),
    refDes: row.reference_designators.join(', '),
  }));
}

function buildDecisions(
  rows: ResolvedRow[],
  choices: RowChoice[],
): CommitDecision[] {
  return rows.map((row, idx) => {
    const choice = choices[idx];
    const refs = choice.refDes
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const qty = Math.max(1, Number(choice.quantity) || 1);
    return {
      row_indices: row.row_indices,
      part_id: choice.partId,
      quantity: qty,
      reference_designators: refs,
    };
  });
}

function Stepper({ step }: { step: Step }) {
  const steps: Array<{ key: Step; label: string }> = [
    { key: 'upload', label: '1. Upload' },
    { key: 'map', label: '2. Map columns' },
    { key: 'review', label: '3. Review & match' },
    { key: 'done', label: '4. Done' },
  ];
  const activeIdx = steps.findIndex((s) => s.key === step);
  return (
    <ol className="flex-wrap items-center gap-2 text-sm">
      {steps.map((s, idx) => (
        <li
          key={s.key}
          className={cn(
            'rounded-md px-3 py-1',
            idx === activeIdx
              ? 'bg-forge-100 dark:bg-forge-900 font-medium text-forge-800 dark:text-forge-200 dark:text-forge-300'
              : idx < activeIdx
                ? 'bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400'
                : 'text-stone-400 dark:text-stone-500',
          )}
        >
          {s.label}
        </li>
      ))}
    </ol>
  );
}

function UploadStep({
  uploading,
  error,
  onSelect,
}: {
  uploading: boolean;
  error: Error | null;
  onSelect: (event: ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <section className="rounded-lg border dashed border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 p-8 text-center">
      <p className="text-sm text-stone-600 dark:text-stone-400">
        Drop or pick a .xlsx or .csv file. KiCad's "Tools → Generate BOM"
        output works as-is.
      </p>
      <label className="mt-4 inline-block cursor-pointer rounded-md bg-forge-600 px-4 py-2 text-sm font-medium text-white hover:bg-forge-700">
        {uploading ? 'Uploading…' : 'Choose file'}
        <input
          type="file"
          accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
          className="hidden"
          onChange={onSelect}
          disabled={uploading}
        />
      </label>
      <p className="mt-3 text-xs text-stone-500 dark:text-stone-400">
        Files are kept in memory for an hour. Max 10 MB.
      </p>
      {error && <p className="mt-4 text-sm text-red-600">{error.message}</p>}
    </section>
  );
}

function MapStep({
  upload,
  mapping,
  onChange,
  onCancel,
  onContinue,
  resolving,
  error,
}: {
  upload: UploadResponse;
  mapping: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  onCancel: () => void;
  onContinue: () => void;
  resolving: boolean;
  error: Error | null;
}) {
  // Reverse the mapping for the column-driven view: each column has
  // at most one BOM field assigned. Wire format stays {field: column}.
  const fieldByColumn = new Map<string, string>();
  for (const [field, column] of Object.entries(mapping)) {
    if (column) fieldByColumn.set(column, field);
  }

  const handleColumnAssign = (column: string, field: string) => {
    const next = { ...mapping };
    for (const [f, c] of Object.entries(next)) {
      if (c === column) delete next[f];
    }
    if (field === '') {
      onChange(next);
      return;
    }
    if (next[field]) delete next[field];
    next[field] = column;
    onChange(next);
  };

  const hasLookup = Boolean(
    mapping.part_number || mapping.mfg_pn || mapping.value,
  );

  const mappedColumnCount = fieldByColumn.size;
  const unmappedColumnCount = upload.columns.filter(
    (c) => c && !fieldByColumn.has(c),
  ).length;

  return (
    <section className="space-y-4">
      <div className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
        <div className="border-b border-stone-200 dark:border-stone-800 px-4 py-3 text-sm">
          <p>
            <span className="font-medium">{upload.filename}</span> —{' '}
            {upload.total_rows} rows, {upload.columns.length} columns.{' '}
            <span className="text-stone-500 dark:text-stone-400">
              {mappedColumnCount} mapped · {unmappedColumnCount} unmapped
            </span>
          </p>
          <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
            One row per spreadsheet column. Pick the matching BOM field
            for each, or leave columns you don't care about set to "skip".
            Map at least one of <span className="font-medium">Part Number</span>,
            <span className="font-medium"> Mfg PN</span>, or{' '}
            <span className="font-medium">Value</span> so the importer can
            look parts up.
          </p>
        </div>
        <table className="w-full text-left text-sm">
          <thead className="bg-stone-50 dark:bg-stone-900 text-xs uppercase text-stone-500 dark:text-stone-400">
            <tr>
              <th className="w-1/2 px-3 py-2">Spreadsheet column</th>
              <th className="px-3 py-2">BOM field</th>
            </tr>
          </thead>
          <tbody>
            {upload.columns.filter(Boolean).map((column) => {
              const assignedField = fieldByColumn.get(column) ?? '';
              const isMapped = assignedField !== '';
              return (
                <tr
                  key={column}
                  className="border-t border-stone-100 dark:border-stone-800"
                >
                  <td className="px-3 py-2 align-top">
                    <div className="flex items-center gap-2">
                      <span
                        aria-hidden
                        className={
                          isMapped
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-stone-300 dark:text-stone-700'
                        }
                        title={isMapped ? 'mapped' : 'unmapped — will be ignored'}
                      >
                        {isMapped ? '✓' : '○'}
                      </span>
                      <span className="font-mono text-stone-700 dark:text-stone-300">
                        {column}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={assignedField}
                      onChange={(e) =>
                        handleColumnAssign(column, e.target.value)
                      }
                      className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2 py-1.5 text-sm"
                    >
                      <option value="">— skip this column —</option>
                      {upload.supported_fields.map((descriptor) => {
                        const otherColumn = mapping[descriptor.field];
                        const claimedElsewhere =
                          otherColumn && otherColumn !== column;
                        return (
                          <option key={descriptor.field} value={descriptor.field}>
                            {descriptor.label}
                            {claimedElsewhere
                              ? `  (currently from "${otherColumn}")`
                              : ''}
                          </option>
                        );
                      })}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-4">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">
          Sample rows
        </h3>
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead>
              <tr className="text-stone-500 dark:text-stone-400">
                {upload.columns.map((c) => (
                  <th
                    key={c}
                    className="border-b border-stone-200 dark:border-stone-800 px-2 py-1 text-left font-medium"
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {upload.sample_rows.map((row, idx) => (
                <tr key={idx}>
                  {upload.columns.map((c) => (
                    <td
                      key={c}
                      className="border-b border-stone-100 dark:border-stone-800 px-2 py-1 align-top text-stone-700 dark:text-stone-300"
                    >
                      {row[c] !== undefined && row[c] !== null
                        ? String(row[c])
                        : ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {!hasLookup && (
        <p className="text-sm text-amber-700">
          Tip: for Aaron-style BOMs (KiCad with the part number baked into the
          symbol) map <span className="font-mono">part_number</span>. For a
          generic KiCad export, map <span className="font-mono">value</span>{' '}
          and <span className="font-mono">package</span> so the importer can
          suggest candidates.
        </p>
      )}
      {error && <p className="text-sm text-red-600">{error.message}</p>}

      <div className="flex justify-between">
        <Button variant="ghost" onClick={onCancel}>
          ← Pick a different file
        </Button>
        <Button onClick={onContinue} disabled={!hasLookup || resolving}>
          {resolving ? 'Resolving…' : 'Resolve parts →'}
        </Button>
      </div>
    </section>
  );
}

function ReviewStep({
  orgSlug,
  resolved,
  choices,
  onChoicesChange,
  onBack,
  onCommit,
  committing,
  error,
  preview,
}: {
  orgSlug: string | undefined;
  resolved: ResolveResponse;
  choices: RowChoice[];
  onChoicesChange: (next: RowChoice[]) => void;
  onBack: () => void;
  onCommit: () => void;
  committing: boolean;
  error: Error | null;
  preview: CommitResponse | null;
}) {
  const updateChoice = (idx: number, patch: Partial<RowChoice>) => {
    const next = [...choices];
    next[idx] = { ...next[idx], ...patch };
    onChoicesChange(next);
  };

  const willAdd = choices.filter((c) => c.partId !== null).length;
  const willSkip = choices.length - willAdd;

  return (
    <section className="space-y-4">
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          label="Matched"
          value={resolved.matched_count}
          tone={resolved.matched_count > 0 ? 'green' : undefined}
        />
        <Stat
          label="Ambiguous"
          value={resolved.ambiguous_count}
          tone={resolved.ambiguous_count > 0 ? 'amber' : undefined}
        />
        <Stat
          label="Unmatched"
          value={resolved.unmatched_count}
          tone={resolved.unmatched_count > 0 ? 'red' : undefined}
        />
        <Stat label="Will add" value={willAdd} tone="green" />
      </dl>

      <div className="overflow-hidden rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
        <table className="w-full text-left text-sm">
          <thead className="bg-stone-50 dark:bg-stone-900 text-xs uppercase text-stone-500 dark:text-stone-400">
            <tr>
              <th className="px-3 py-2">From CSV</th>
              <th className="px-3 py-2">Refs</th>
              <th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2">Resolve to</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {resolved.rows.map((row, idx) => (
              <ReviewRow
                key={idx}
                orgSlug={orgSlug}
                row={row}
                choice={choices[idx]}
                onChange={(patch) => updateChoice(idx, patch)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {preview?.dry_run && (
        <p className="text-sm text-stone-600 dark:text-stone-400">
          Dry-run preview: would add {preview.added}, skip {preview.skipped},
          error on {preview.errored}.
        </p>
      )}
      {error && <p className="text-sm text-red-600">{error.message}</p>}

      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack}>
          ← Back to mapping
        </Button>
        <div className="flex gap-2">
          <Button onClick={onCommit} disabled={committing || willAdd === 0}>
            {committing
              ? 'Importing…'
              : `Add ${willAdd} line${willAdd === 1 ? '' : 's'} (skip ${willSkip})`}
          </Button>
        </div>
      </div>
    </section>
  );
}

function ReviewRow({
  orgSlug,
  row,
  choice,
  onChange,
}: {
  orgSlug: string | undefined;
  row: ResolvedRow;
  choice: RowChoice;
  onChange: (patch: Partial<RowChoice>) => void;
}) {
  const tone =
    row.status === 'matched'
      ? 'border border-l-4 border-l-green-400'
      : row.status === 'ambiguous'
        ? 'border border-l-4 border-l-amber-400'
        : 'border border-l-4 border-l-red-400';

  return (
    <tr className={cn('border-t border-stone-100 dark:border-stone-800 align-top', tone)}>
      <td className="px-3 py-2">
        <div className="font-mono text-xs text-stone-500 dark:text-stone-400">
          rows {row.row_indices.join(', ')}
        </div>
        <div className="text-stone-800 dark:text-stone-200">
          {row.raw_part_number && (
            <span className="font-mono text-stone-900 dark:text-stone-100">
              {row.raw_part_number}
            </span>
          )}
          {row.raw_mfg_pn && (
            <span className="ml-2 font-mono text-xs text-stone-700 dark:text-stone-300">
              {row.raw_mfg_pn}
            </span>
          )}
        </div>
        {(row.raw_value || row.raw_package) && (
          <div className="text-xs text-stone-500 dark:text-stone-400">
            {[row.raw_value, row.raw_package].filter(Boolean).join(' · ')}
          </div>
        )}
        {row.raw_description && (
          <div className="text-xs text-stone-500 dark:text-stone-400">{row.raw_description}</div>
        )}
      </td>
      <td className="px-3 py-2">
        <Input
          value={choice.refDes}
          onChange={(e) => onChange({ refDes: e.target.value })}
          className="w-32 font-mono text-xs"
        />
      </td>
      <td className="px-3 py-2 text-right">
        <Input
          type="number"
          min={1}
          value={choice.quantity}
          onChange={(e) => onChange({ quantity: e.target.value })}
          className="w-20 text-right"
        />
      </td>
      <td className="px-3 py-2">
        <ResolveControl
          orgSlug={orgSlug}
          row={row}
          choice={choice}
          onChange={onChange}
        />
      </td>
      <td className="px-3 py-2 text-right">
        <button
          type="button"
          onClick={() =>
            onChange({
              partId: choice.partId === null ? row.matched_part?.part_id ?? null : null,
            })
          }
          className={cn(
            'text-xs',
            choice.partId === null
              ? 'text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-200'
              : 'text-red-500 hover:text-red-700',
          )}
        >
          {choice.partId === null ? 'include' : 'skip'}
        </button>
      </td>
    </tr>
  );
}

function ResolveControl({
  orgSlug,
  row,
  choice,
  onChange,
}: {
  orgSlug: string | undefined;
  row: ResolvedRow;
  choice: RowChoice;
  onChange: (patch: Partial<RowChoice>) => void;
}) {
  if (choice.pickerActive) {
    return (
      <div className="space-y-1">
        <PartPicker
          orgSlug={orgSlug}
          selected={choice.pickerSelected}
          onSelect={(part) =>
            onChange({
              pickerSelected: part,
              partId: part?.id ?? null,
            })
          }
        />
        <button
          type="button"
          onClick={() =>
            onChange({
              pickerActive: false,
              pickerSelected: null,
              partId: row.matched_part?.part_id ?? null,
            })
          }
          className="text-xs text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-200"
        >
          ← back to suggestions
        </button>
      </div>
    );
  }

  const allCandidates: CandidatePart[] = [];
  if (row.matched_part) allCandidates.push(row.matched_part);
  for (const c of row.candidates) {
    if (!allCandidates.some((existing) => existing.part_id === c.part_id)) {
      allCandidates.push(c);
    }
  }

  return (
    <div className="space-y-1">
      {allCandidates.length === 0 ? (
        <p className="text-xs text-stone-500 dark:text-stone-400">
          {row.message ?? 'No matches in this org.'}
        </p>
      ) : (
        <select
          value={choice.partId ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            onChange({ partId: v === '' ? null : Number(v) });
          }}
          className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2 py-1.5 text-sm"
        >
          <option value="">— skip —</option>
          {allCandidates.map((c) => (
            <option key={c.part_id} value={c.part_id}>
              {c.part_number}
              {c.mfg_pn ? ` · ${c.mfg_pn}` : ''}
              {c.value || c.package
                ? ` (${[c.value, c.package].filter(Boolean).join(' · ')})`
                : ''}
            </option>
          ))}
        </select>
      )}
      <button
        type="button"
        onClick={() =>
          onChange({
            pickerActive: true,
            pickerSelected: null,
            partId: null,
          })
        }
        className="text-xs text-forge-600 hover:text-forge-800 dark:hover:text-forge-300"
      >
        Pick a different part…
      </button>
      {row.status === 'matched' && choice.partId === row.matched_part?.part_id && (
        <p className="text-xs text-green-700">✓ Matched on lookup</p>
      )}
    </div>
  );
}

function DoneStep({
  result,
  onBack,
}: {
  result: CommitResponse;
  onBack: () => void;
}) {
  return (
    <section className="space-y-4">
      <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-900">
        <p className="font-medium">
          Added {result.added} line{result.added === 1 ? '' : 's'} to the
          revision.
        </p>
        {result.skipped > 0 && (
          <p className="mt-1">
            Skipped {result.skipped} line{result.skipped === 1 ? '' : 's'}.
          </p>
        )}
        {result.errored > 0 && (
          <p className="mt-1 text-red-700">
            {result.errored} row{result.errored === 1 ? '' : 's'} errored.
          </p>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-stone-50 dark:bg-stone-900 text-xs uppercase text-stone-500 dark:text-stone-400">
              <tr>
                <th className="px-3 py-2">Rows</th>
                <th className="px-3 py-2">Action</th>
                <th className="px-3 py-2">Part #</th>
                <th className="px-3 py-2 text-right">Qty</th>
                <th className="px-3 py-2">Refs</th>
                <th className="px-3 py-2">Note</th>
              </tr>
            </thead>
            <tbody>
              {result.outcomes.map((o, idx) => (
                <tr key={idx} className="border-t border-stone-100 dark:border-stone-800">
                  <td className="px-3 py-1.5 font-mono text-xs text-stone-500 dark:text-stone-400">
                    {o.row_indices.join(', ')}
                  </td>
                  <td className="px-3 py-1.5">
                    <ActionBadge action={o.action} />
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs">
                    {o.part_number ?? '—'}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono">
                    {o.quantity}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs">
                    {o.reference_designators.join(', ') || '—'}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-stone-600 dark:text-stone-400">
                    {o.message ?? ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Button onClick={onBack}>Back to BOM</Button>
    </section>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'green' | 'red' | 'amber';
}) {
  return (
    <div className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-3">
      <dt className="text-xs uppercase tracking-wide text-stone-500 dark:text-stone-400">{label}</dt>
      <dd
        className={cn(
          'text-2xl font-semibold',
          tone === 'green' && value > 0 && 'text-green-700',
          tone === 'red' && value > 0 && 'text-red-700',
          tone === 'amber' && value > 0 && 'text-amber-700',
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function ActionBadge({ action }: { action: CommitResponse['outcomes'][number]['action'] }) {
  const styles: Record<typeof action, string> = {
    added: 'bg-green-100 text-green-800',
    skipped: 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400',
    errored: 'bg-red-100 text-red-800',
  };
  return (
    <span
      className={cn(
        'inline-flex rounded px-2 py-0.5 text-xs font-medium',
        styles[action],
      )}
    >
      {action}
    </span>
  );
}
